import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { CONTACT_INFO } from '@/lib/constants';
import TrackedBookingLink from './TrackedBookingLink';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Programs', href: '/brazilian-jiu-jitsu-training-programs' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Schedule', href: '/schedule' },
    { name: 'Instructors', href: '/instructors' },
    { name: 'About', href: '/about-delta-bjj-culver-city' },
    { name: 'Contact', href: '/contact-bjj-culver-city' },
  ];

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 w-full z-[100] transition-all duration-500"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 100,
        }}
      >
        {/* Background layer that always covers full width */}
        <div 
          className={`absolute inset-0 transition-all duration-500 pointer-events-none ${
            scrolled ? 'bg-brand-black/60 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
          }`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
          }}
        />
        
        {/* Content container */}
        <div 
          className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex justify-between items-center transition-all duration-500 ${
            scrolled ? 'py-3 md:py-4' : 'py-4 md:py-8'
          }`}
        >
          {/* Logo */}
          <Link to="/" className="relative z-[101] flex items-center gap-3 group">
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center p-0.5">
              <img 
                src="/images/delta-logo.svg" 
                alt="Delta BJJ" 
                className="w-full h-full transform group-hover:rotate-[360deg] transition-transform duration-1000" 
                style={{ 
                  filter: 'invert(21%) sepia(82%) saturate(1966%) hue-rotate(205deg) brightness(91%) contrast(92%)',
                  transformOrigin: '50% 60%'
                }}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-display font-extrabold text-lg sm:text-xl tracking-tighter leading-none">
                DELTA
              </span>
              <span className="text-brand-silver font-display text-[9px] sm:text-[10px] tracking-[0.22em] sm:tracking-[0.3em] leading-none mt-1 uppercase font-extrabold">
                BRAZILIAN JIU-JITSU
              </span>
            </div>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-xs md:text-sm uppercase tracking-[0.25em] font-bold transition-all relative group ${
                  location.pathname === link.href ? 'text-white' : 'text-white/50 hover:text-white'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[2px] bg-brand-blue transition-all duration-300 ${location.pathname === link.href ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </Link>
            ))}
            
            <div className="h-4 w-[1px] bg-white/10 mx-2" />
            
            <div className="flex items-center gap-4">
              {/* Primary CTA: Book Now */}
              <TrackedBookingLink 
                href="/#book-trial" 
                className="flex items-center gap-2 px-7 py-3 bg-brand-blue text-white rounded-sm text-xs md:text-sm uppercase tracking-[0.2em] font-extrabold hover:bg-blue-600 transition-all shadow-lg shadow-brand-blue/20"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Book Now</span>
              </TrackedBookingLink>

              {/* Secondary CTA: Call Now */}
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-sm text-xs md:text-sm text-white uppercase tracking-[0.2em] font-extrabold hover:bg-white/10 transition-all"
              >
                <Phone className="w-4 h-4 text-brand-blue" />
                <span>Call Now</span>
              </a>
            </div>
          </div>

          {/* Mobile Toggle */}
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden relative z-[101] text-white p-2"
          >
            {isOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: '-100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 200 }}
            className="fixed top-0 left-0 right-0 bottom-0 bg-brand-black z-[99] lg:hidden flex flex-col justify-center items-center gap-10 px-6"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              zIndex: 99,
            }}
          >
            <div className="flex flex-col items-center gap-6 mt-20 w-full max-w-sm">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * (i + 1) }}
                >
                  <Link
                    to={link.href}
                    className="text-3xl sm:text-4xl font-display font-bold text-white hover:text-brand-blue transition-colors tracking-tighter uppercase"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col items-center gap-6 w-full max-w-sm mt-8"
            >
              <div className="w-12 h-[1px] bg-brand-blue" />
              <TrackedBookingLink 
                href="/#book-trial" 
                className="w-full text-center py-4 bg-brand-blue text-white font-display font-extrabold text-lg tracking-widest uppercase rounded-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book Now
              </TrackedBookingLink>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                className="text-white/60 text-[10px] tracking-[0.3em] uppercase hover:text-white transition-colors flex items-center gap-2"
              >
                <Phone className="w-3 h-3 text-brand-blue" />
                <span>Call Now</span>
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
